import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Star, CheckCircle, Loader2, MapPin, Calendar, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

export const EvaluatePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bookingId = searchParams.get('booking');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [alreadyEvaluated, setAlreadyEvaluated] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!bookingId) {
      navigate('/');
      return;
    }

    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/api/evaluations/booking/${bookingId}`);
        if (response.data.already_evaluated) {
          setAlreadyEvaluated(true);
          setRating(response.data.rating);
        } else {
          setBooking(response.data.booking);
        }
      } catch (error) {
        toast.error("Course non trouvée");
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, navigate]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/evaluations`, {
        booking_id: bookingId,
        rating,
        comment: comment.trim() || null
      });
      setSuccess(true);
      toast.success("Merci pour votre évaluation !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (alreadyEvaluated) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('eval_already')}</h1>
            <p className="text-zinc-400 mb-4">{t('eval_already_desc')}</p>
            <div className="flex justify-center gap-1 mb-6">
              {[1,2,3,4,5].map(i => (
                <Star 
                  key={i} 
                  className={`w-8 h-8 ${i <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`} 
                />
              ))}
            </div>
            <Link to="/">
              <Button className="btn-primary w-full">{t('eval_back')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('eval_thanks')}</h1>
            <p className="text-zinc-400 mb-4">{t('eval_thanks_desc')}</p>
            <div className="flex justify-center gap-1 mb-6">
              {[1,2,3,4,5].map(i => (
                <Star 
                  key={i} 
                  className={`w-8 h-8 ${i <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}`} 
                />
              ))}
            </div>
            
            {rating >= 4 && (
              <div className="mb-6 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <p className="text-emerald-400 text-sm mb-3">
                  {t('eval_happy')} 
                </p>
                <a 
                  href="https://g.page/r/CXCi3jy1-jW0EAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full font-medium text-sm hover:bg-zinc-200 transition-all"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('eval_google')}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
            
            <Link to="/">
              <Button className="btn-primary w-full">{t('eval_back')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 bg-emerald-500/20 rounded-xl">
              <Star className="w-6 h-6 text-emerald-500 fill-emerald-500" />
            </div>
            <span className="text-emerald-500 font-bold text-xl">YOUMI GREEN</span>
            <span className="text-white font-bold text-xl">VTC</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('eval_title')}</h1>
          <p className="text-zinc-400">{t('eval_subtitle')}</p>
        </div>

        <div className="glass-card rounded-2xl p-6">
          {/* Booking Info */}
          {booking && (
            <div className="bg-zinc-900 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-zinc-500 text-xs">{t('eval_departure')}</p>
                  <p className="text-white text-sm">{booking.pickup_address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-zinc-500 text-xs">{t('eval_arrival')}</p>
                  <p className="text-white text-sm">{booking.dropoff_address}</p>
                </div>
              </div>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calendar className="w-4 h-4" />
                  {formatDate(booking.pickup_datetime)}
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Clock className="w-4 h-4" />
                  {formatTime(booking.pickup_datetime)}
                </div>
              </div>
            </div>
          )}

          {/* Star Rating */}
          <div className="text-center mb-6">
            <p className="text-zinc-400 mb-4">{t('eval_rate')}</p>
            <div className="flex justify-center gap-2">
              {[1,2,3,4,5].map(i => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRating(i)}
                  onMouseEnter={() => setHoverRating(i)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${i}`}
                >
                  <Star 
                    className={`w-10 h-10 transition-colors ${
                      i <= (hoverRating || rating) 
                        ? 'text-yellow-500 fill-yellow-500' 
                        : 'text-zinc-600'
                    }`} 
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-emerald-400 mt-2 font-medium">
                {rating === 5 && t('eval_5')}
                {rating === 4 && t('eval_4')}
                {rating === 3 && t('eval_3')}
                {rating === 2 && t('eval_2')}
                {rating === 1 && t('eval_1')}
              </p>
            )}
          </div>

          {/* Comment */}
          <div className="mb-6">
            <label className="block text-zinc-400 text-sm mb-2">
              {t('eval_comment')}
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('eval_placeholder')}
              className="bg-zinc-900 border-zinc-700 text-white resize-none"
              rows={3}
              data-testid="comment-input"
            />
          </div>

          {/* Submit */}
          <Button 
            onClick={handleSubmit}
            disabled={rating === 0 || submitting}
            className="w-full btn-primary"
            data-testid="submit-evaluation"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              t('eval_submit')
            )}
          </Button>
        </div>

        <p className="text-zinc-500 text-sm text-center mt-6">
          YOUMI GREEN VTC - Service VTC 100% électrique
        </p>
      </div>
    </div>
  );
};

export default EvaluatePage;
